import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  multFieldLine: {
    display: "flex",
    gap: theme.spacing(2),
    "& > *": { flex: 1 },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const AppointmentSchema = Yup.object().shape({
  title: Yup.string().min(2, "Muito curto").required("Obrigatório"),
  startAt: Yup.string().required("Obrigatório"),
  endAt: Yup.string().required("Obrigatório"),
});

const initialState = {
  title: "",
  description: "",
  location: "",
  startAt: "",
  endAt: "",
  queueId: "",
  userId: "",
};

// Compromisso vinculado a um cliente (ticket) - diferente do
// "Agendamentos" ja existente (que agenda o ENVIO de uma mensagem, ver
// ScheduleModal). Esse aqui cria um evento na agenda unica da empresa,
// sincronizado com o Google Calendar quando conectado (Configuracoes >
// Integracoes). Ver AppointmentServices no backend.
// appointment (opcional): quando informado, o modal edita esse
// compromisso existente (PUT) em vez de criar um novo (POST). Vem da
// tela de Agendamentos (pages/Schedules) quando o usuario clica no
// lapis de um compromisso ja criado - antes esse modal so' sabia
// criar, entao clicar num compromisso existente pra editar nao fazia
// nada (bug real reportado 2026-08-27).
const AppointmentModal = ({ open, onClose, ticketId, contactId, cardId, appointment, reload }) => {
  const classes = useStyles();
  const isEditing = Boolean(appointment && appointment.id);
  // Compromisso criado direto no Google Calendar (nunca passou pelo
  // Voicechat) ainda nao tem linha local - id sintetico "google-<eventId>"
  // (ver ListAppointmentsService). Editar e salvar "adota" esse evento em
  // vez de dar PUT num id que nao existe na tabela. Fix 2026-09-16.
  const isGoogleOnly = isEditing && String(appointment.id).startsWith("google-");
  const [formValues, setFormValues] = useState(initialState);
  const [queues, setQueues] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [queuesRes, usersRes] = await Promise.all([
          api.get("/queue"),
          api.get("/users/"),
        ]);
        setQueues(queuesRes.data);
        setUsers(usersRes.data.users || usersRes.data);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (isEditing) {
      setFormValues({
        title: appointment.title || "",
        description: appointment.description || "",
        location: appointment.location || "",
        startAt: moment(appointment.startAt).format("YYYY-MM-DDTHH:mm"),
        endAt: moment(appointment.endAt).format("YYYY-MM-DDTHH:mm"),
        queueId: appointment.queueId || "",
        userId: appointment.userId || "",
      });
    } else {
      setFormValues(initialState);
    }
  }, [open, isEditing, appointment]);

  const handleClose = () => {
    setFormValues(initialState);
    onClose();
  };

  const handleSaveAppointment = async (values) => {
    try {
      const payload = {
        ...values,
        queueId: values.queueId || null,
        userId: values.userId || null,
        startAt: new Date(values.startAt).toISOString(),
        endAt: new Date(values.endAt).toISOString(),
      };

      if (isGoogleOnly) {
        await api.post("/appointments/adopt-google-event", {
          ...payload,
          contactId,
          googleEventId: appointment.googleEventId,
          googleCalendarId: appointment.googleCalendarId,
        });
        toast.success("Compromisso atualizado com sucesso!");
      } else if (isEditing) {
        await api.put(`/appointments/${appointment.id}`, payload);
        toast.success("Compromisso atualizado com sucesso!");
      } else {
        await api.post("/appointments", { ...payload, contactId, ticketId, cardId });
        toast.success("Compromisso agendado com sucesso!");
      }
      reload && reload();
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>{isEditing ? "Editar compromisso" : "Agendar compromisso"}</DialogTitle>
      <Formik
        initialValues={formValues}
        enableReinitialize
        validationSchema={AppointmentSchema}
        onSubmit={async (values, actions) => {
          // Fix 2026-09-28: antes disparava handleSaveAppointment() sem
          // esperar e ja liberava o botao (isSubmitting=false) depois de
          // so' 300ms fixos - a chamada de rede real podia ainda estar em
          // andamento, entao um segundo clique de impaciencia no botao
          // (ainda visivelmente habilitado) disparava OUTRO save completo.
          // Foi exatamente a causa de 4 compromissos identicos ("EMERSON
          // VISITA COMERCIAL") criados em 7 segundos, cada um com seu
          // proprio evento no Google Calendar. Agora so' libera o botao
          // quando o save de verdade terminou (sucesso ou erro).
          try {
            await handleSaveAppointment(values);
          } finally {
            actions.setSubmitting(false);
          }
        }}
      >
        {({ touched, errors, isSubmitting }) => (
          <Form>
            <DialogContent dividers>
              <Field
                as={TextField}
                label="Título"
                name="title"
                autoFocus
                fullWidth
                margin="dense"
                variant="outlined"
                error={touched.title && Boolean(errors.title)}
                helperText={touched.title && errors.title}
              />
              <Field
                as={TextField}
                label="Descrição"
                name="description"
                fullWidth
                multiline
                minRows={2}
                margin="dense"
                variant="outlined"
              />
              <Field
                as={TextField}
                label="Local"
                name="location"
                fullWidth
                margin="dense"
                variant="outlined"
              />
              <div className={classes.multFieldLine} style={{ marginTop: 8 }}>
                <Field
                  as={TextField}
                  label="Início"
                  name="startAt"
                  type="datetime-local"
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  error={touched.startAt && Boolean(errors.startAt)}
                  helperText={touched.startAt && errors.startAt}
                />
                <Field
                  as={TextField}
                  label="Fim"
                  name="endAt"
                  type="datetime-local"
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  error={touched.endAt && Boolean(errors.endAt)}
                  helperText={touched.endAt && errors.endAt}
                />
              </div>
              <div className={classes.multFieldLine} style={{ marginTop: 8 }}>
                <FormControl variant="outlined" margin="dense" fullWidth>
                  <InputLabel id="appointment-queue-label">Setor</InputLabel>
                  <Field
                    as={Select}
                    label="Setor"
                    name="queueId"
                    labelId="appointment-queue-label"
                  >
                    <MenuItem value="">
                      <em>Nenhum</em>
                    </MenuItem>
                    {queues.map((q) => (
                      <MenuItem key={q.id} value={q.id}>
                        {q.name}
                      </MenuItem>
                    ))}
                  </Field>
                </FormControl>
                <FormControl variant="outlined" margin="dense" fullWidth>
                  <InputLabel id="appointment-user-label">Responsável</InputLabel>
                  <Field
                    as={Select}
                    label="Responsável"
                    name="userId"
                    labelId="appointment-user-label"
                  >
                    <MenuItem value="">
                      <em>Nenhum</em>
                    </MenuItem>
                    {users.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.name}
                      </MenuItem>
                    ))}
                  </Field>
                </FormControl>
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} color="secondary" disabled={isSubmitting}>
                Cancelar
              </Button>
              <div className={classes.btnWrapper}>
                <Button type="submit" color="primary" variant="contained" disabled={isSubmitting}>
                  {isEditing ? "Salvar" : "Agendar"}
                </Button>
                {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
              </div>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AppointmentModal;
